// @flow
import * as R from "ramda"
import { createSelector } from "reselect"

import {
  passengersCountSelector,
  passengersValuesSelector,
} from "booking/services/reservation/selectors/passengersSelectors"
import { getJourney } from "booking/services/journey/reducer"
import Price from "common/records/Price"
import { priceMapper, serverPriceMapper } from "common/utils/priceUtils"
import { QUICK_OPTION_SERVER_MAPPER, QUICK_OPTION_STATE_MAPPER } from "./mapper"
import { SEATING_OPTIONS } from "./reducer"
import type { SeatingState, SelectedSegmentOption } from "./reducer"
import type { SeatingFromServer, SeatingOptions, QuickSeatingOptions } from "./types"
import type { Flight } from "common/records/Flight/Flight"
import type { Trip } from "common/records/Trip/Trip"

/**
 * Selector that returns state object of the seating
 * @param {object} state - state of the app
 */
export const seatingStateSelector = (state: any): SeatingState =>
  R.path(["reservation", "additionalServices", "seating"], state)

/**
 * Selector that returns boolean SEATING a/b test value
 * @param {object} state - state of the app
 */
export const isEligibleForSeatingSelector = (state: any): boolean =>
  state.splitster.get("SEATING").value === "show"

/**
 * Selector that returns seating data received from server
 * @param {object} state - state of the app
 */
export const seatingDataSelector: $FlowFixMe = createSelector(
  seatingStateSelector,
  R.prop("data"),
)

/**
 * Selector that returns seating status
 * @param {object} state - state of the app
 */
export const seatingStatusSelector: $FlowFixMe = createSelector(
  seatingStateSelector,
  R.prop("status"),
)

/**
 * Selector that returns status of request
 * @param {object} state - state of the app
 */
export const seatingRequestStatusSelector: $FlowFixMe = createSelector(
  seatingStateSelector,
  R.prop("requestStatus"),
)

/**
 * Selector that returns if seating is loading
 * @param {object} state - state of the app
 */
export const isSeatingLoadingSelector: $FlowFixMe = createSelector(
  seatingStatusSelector,
  status => status === "loading",
)

/**
 * Selector that returns if seating is error
 * @param {object} state - state of the app
 */
export const isSeatingErrorSelector: $FlowFixMe = createSelector(
  seatingStatusSelector,
  status => status === "error",
)

/**
 * Selector that returns if seating is valid
 * @param {object} state - state of the app
 */
export const isSeatingValidSelector: $FlowFixMe = createSelector(
  seatingStatusSelector,
  status => status === "valid",
)

/**
 * Selector that returns if seating is valid
 * @param {object} state - state of the app
 */
export const isSeatingEmptySelector: $FlowFixMe = createSelector(
  seatingStatusSelector,
  status => status === "empty",
)

/**
 * Selector that returns true if seating data is loading or there is seating for
 * at least 1 segment -> seating layout should be displayed
 * @param {object} state - state of the app
 */
export const shouldDisplaySeating: $FlowFixMe = createSelector(
  seatingDataSelector,
  isSeatingValidSelector,
  isSeatingLoadingSelector,
  (seatingData, isValid, isLoading) =>
    Boolean(isLoading || (isValid && seatingData && seatingData.length > 0)),
)

/**
 * Selector that returns selectedSegmentOptions object of the seating
 * @param {object} state - state of the app
 */
export const selectedSegmentOptionsSelector: $FlowFixMe = createSelector(
  seatingStateSelector,
  R.prop("selectedSegmentsOptions"),
)

/**
 * Selector that returns if seating is added for at least 1 segment
 * @param {object} state - state of the app
 */
export const isSeatingAddedSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  selectedSegmentOptions => {
    const filter = val => val.selectedOption !== SEATING_OPTIONS.noReservation
    return Boolean(R.keys(R.pickBy(filter, selectedSegmentOptions)).length)
  },
)

const getSegmentQuickOptionsPrices = (
  segmentCode: string,
  seatingData: ?SeatingFromServer,
): { [quickOption: QuickSeatingOptions]: Price } => {
  const selectedSegmentData =
    seatingData && R.find(R.propEq("segment_code", segmentCode))(seatingData)
  if (!selectedSegmentData) {
    return {}
  }
  const segmentQuickOptions = R.prop("quick_options")(selectedSegmentData)
  return segmentQuickOptions.reduce((quickOptions, quickOption) => {
    // eslint-disable-next-line no-param-reassign
    quickOptions[QUICK_OPTION_SERVER_MAPPER[quickOption.option]] = new Price({
      ...priceMapper(quickOption.price),
    })
    return quickOptions
  }, {})
}

const resolvePriceForSelectedOption = (
  selectedOption: SeatingOptions,
  quickOptionsPrices: { [quickOption: QuickSeatingOptions]: Price },
): Price => {
  // TODO: return price of seatMap price selector when implemented
  // if (selectedOption === "seatMap") {
  //   return new Price({ amount: 0 })
  // }
  if (R.values(QUICK_OPTION_SERVER_MAPPER).includes(selectedOption)) {
    // $FlowFixMe flow does not recognize that noReservation is not in QUICK_OPTION_SERVER_MAPPER values
    return quickOptionsPrices[selectedOption]
  }
  // noReservation is selected
  return selectedOption && new Price({ amount: 0 })
}

type CodeAndName = { code: string, name: string }
type ExtraFlightData = {
  operatingArline: CodeAndName,
  arrival: ?CodeAndName,
  departure: ?CodeAndName,
}
const getFlightData = (flight: Flight): ExtraFlightData => ({
  operatingArline: flight.airline,
  arrival: flight.arrival ? flight.arrival.where : {},
  departure: flight.departure ? flight.departure.where : {},
})

/**
 * Selector that returns array of segments allegeable for seating with their selected price, flight
 * and passenger info. Intended for rendering reservation bill
 * @param {object} state - state of the app
 */
export const seatingReservationBillSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  getJourney,
  passengersCountSelector,
  isEligibleForSeatingSelector,
  (segmentOptions, seatingData, journey, paxCount, isEligibleForSeating) => {
    if (!isEligibleForSeating || !seatingData) {
      return []
    }
    return R.keys(segmentOptions).map(segmentCode => ({
      ...getFlightData(journey.getFlightById(segmentCode).toJS()),
      segmentCode,
      paxCount,
      segmentCurrentPrice: resolvePriceForSelectedOption(
        segmentOptions[segmentCode].selectedOption,
        getSegmentQuickOptionsPrices(segmentCode, seatingData),
      ),
    }))
  },
)

/**
 * Selector that returns array of segments allegeable for seating with their selected option, flight
 * and passenger info. Intended for rendering seating overview on the booking 3rd step
 * @param {object} state - state of the app
 */
export const seatingOverviewSelectors: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  getJourney,
  passengersCountSelector,
  passengersValuesSelector,
  isEligibleForSeatingSelector,
  (segmentOptions, seatingData, journey, paxCount, paxValues, isEligibleForSeating) => {
    if (!isEligibleForSeating || !seatingData) {
      return []
    }
    const seatingOverview = R.keys(segmentOptions).map(segmentCode => ({
      ...getFlightData(journey.getFlightById(segmentCode).toJS()),
      segmentCode,
      paxCount,
      segmentSelectedOption: segmentOptions[segmentCode].selectedOption,
      paxValues,
    }))
    return R.filter(
      segment => segment.segmentSelectedOption !== SEATING_OPTIONS.noReservation,
      seatingOverview,
    )
  },
)

/**
 * Selector that returns sum Price of all selected seating options
 * @param {object} state - state of the app
 */
export const seatingOrderedPriceSelector: $FlowFixMe = createSelector(
  seatingReservationBillSelector,
  reservationBill => {
    const responseCurrency = reservationBill.length
      ? reservationBill[0].segmentCurrentPrice.currency
      : "eur"
    return reservationBill.reduce(
      (sumPrice, segment) => Price.sum([sumPrice, segment.segmentCurrentPrice]),
      new Price({ amount: 0, currency: responseCurrency }),
    )
  },
)

type ExtendedTripSegment = {
  ...ExtraFlightData,
  seatingAvailable: boolean,
  segmentCode: string,
  quickOptionsPrices: Array<[QuickSeatingOptions, Price]>,
}
type ExtendedTripSegments = Array<ExtendedTripSegment>

const getExtendedTripSegments = (
  trip: ?Trip,
  segmentOptions: { [segmentCode: string]: SelectedSegmentOption },
  seatingData: ?SeatingFromServer,
  journey,
  paxCount: number,
): ExtendedTripSegments => {
  if (!trip) {
    return []
  }
  return trip.flights.map(flight => ({
    ...getFlightData(journey.getFlightById(flight.id).toJS()),
    seatingAvailable: R.has(flight.id)(segmentOptions),
    segmentCode: flight.id,
    // $FlowFixMe
    quickOptionsPrices: R.toPairs(getSegmentQuickOptionsPrices(flight.id, seatingData)),
    paxCount,
  }))
}

const getInboundOutboundTripArray = (
  journey,
  segmentOptions: { [segmentCode: string]: SelectedSegmentOption },
  seatingData: ?SeatingFromServer,
  paxCount: number,
  type: "inbound" | "outbound",
): ExtendedTripSegments => {
  if (journey.isMulticity()) {
    return []
  }
  const isInbound = type === "inbound"
  const trip = isInbound
    ? journey.getInboundTrip() && journey.getInboundTrip().toJS()
    : journey.getOutboundTrip() && journey.getOutboundTrip().toJS()
  return getExtendedTripSegments(trip, segmentOptions, seatingData, journey, paxCount)
}

/**
 * Selector that returns array of all outbound trip segments with their quick options prices, flight
 * info and seatingAvailable. Intended for rendering booking of outbound segments seating
 * @param {object} state - state of the app
 */
export const seatingOutboundSegmentsSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  getJourney,
  passengersCountSelector,
  isSeatingLoadingSelector,
  (segmentOptions, seatingData, journey, paxCount, loading) => ({
    loading,
    trip: getInboundOutboundTripArray(journey, segmentOptions, seatingData, paxCount, "outbound"),
  }),
)

/**
 * Selector that returns array of all inbound trip segments with their quick options prices, flight
 * info and seatingAvailable. Intended for rendering booking of inbound segments seating
 * @param {object} state - state of the app
 */
export const seatingInboundSegmentsSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  getJourney,
  passengersCountSelector,
  isSeatingLoadingSelector,
  (segmentOptions, seatingData, journey, paxCount, loading) => ({
    loading,
    trip: getInboundOutboundTripArray(journey, segmentOptions, seatingData, paxCount, "inbound"),
  }),
)

/**
 * Selector that returns array of all multi city trips with their quick options prices, flight info
 * and seatingAvailable. Intended for rendering booking of multi city segments
 * @param {object} state - state of the app
 */
export const seatingMultiCitySegmentsSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  getJourney,
  passengersCountSelector,
  isSeatingLoadingSelector,
  (segmentOptions, seatingData, journey, paxCount, loading) => {
    if (!journey.isMulticity()) {
      return []
    }
    const trips = journey.get("trips").toJS()
    return trips.map(trip => {
      const tripExtendedOptions = getExtendedTripSegments(
        trip,
        segmentOptions,
        seatingData,
        journey,
        paxCount,
      )
      return {
        loading,
        trip: tripExtendedOptions,
        title: {
          departure: R.path(["departure", "name"], tripExtendedOptions[0]),
          arrival: R.path(["arrival", "name"], R.last(tripExtendedOptions)),
        },
      }
    })
  },
)

/**
 * Selector that returns an array in a shape that is accepted by the backend API.
 * Intended to use only as a part of final save booking body parameter.
 */
export const seatingSaveBookingSerializerSelector: $FlowFixMe = createSelector(
  selectedSegmentOptionsSelector,
  seatingDataSelector,
  isEligibleForSeatingSelector,
  (selectedOptions, seatingData, isEligibleForSeating) => {
    if (!isEligibleForSeating || !selectedOptions) return null
    return Object.keys(selectedOptions).reduce((selections, segmentKey) => {
      const option = selectedOptions[segmentKey].selectedOption
      // If Random is selected don't return any data for segment
      if (option === SEATING_OPTIONS.noReservation) return selections
      // TODO: Add what to return if seatMap is selected
      if (option === SEATING_OPTIONS.seatMap) return selections
      return selections.concat({
        segment_code: segmentKey,
        option: QUICK_OPTION_STATE_MAPPER[selectedOptions[segmentKey].selectedOption],
        price: serverPriceMapper(getSegmentQuickOptionsPrices(segmentKey, seatingData)[option]),
      })
    }, [])
  },
)
